/**
 * Pure helpers for validating and parsing a manually typed US street address.
 *
 * The Google autocomplete path builds structured fields from
 * `place.address_components`. When a user types the address themselves (or
 * edits the value Google produced) we have only a flat string, so we parse it
 * here. The previous single regex was far stricter than real US addressing and
 * rejected many deliverable addresses (hyphenated cities, periods, apostrophes,
 * fractional street numbers, ZIP+4, units, and Google's own
 * `..., ST 12345, USA` shape). See ticket #5486.
 */

const PO_BOX_PATTERN = /\b(?:p\.?\s*o\.?\s*box|post\s+office\s+box)\b/i;
const ZIP_PATTERN = /^\d{5}(?:-\d{4})?$/;
const STATE_PATTERN = /^[A-Za-z]{2}$/;

/**
 * Parse a flat US address string into structured fields.
 *
 * @param {string} raw The raw address string.
 * @returns {{address: string, city: string, state: string, zip: string, formatted: string}|null}
 *          Structured place data, or null when the string is not a parseable,
 *          deliverable US street address.
 */
export function parseUsAddress(raw) {
    if (typeof raw !== 'string') {
        return null;
    }

    let value = raw.trim();
    if (!value) {
        return null;
    }

    // PO boxes are explicitly not accepted during signup.
    if (PO_BOX_PATTERN.test(value)) {
        return null;
    }

    // Drop a trailing country segment ("..., USA" / "United States") that
    // Google's formatted_address appends.
    value = value.replace(/[,\s]*(?:USA|U\.?S\.?A\.?|United\s+States)\.?\s*$/i, '');

    // Normalise the "ST 12345" shape (state and zip with no comma between them,
    // as in Google's formatted_address) into "ST, 12345" so the comma split
    // below yields discrete parts.
    value = value.replace(/[,\s]+([A-Za-z]{2})\s+(\d{5}(?:-\d{4})?)\s*$/, ', $1, $2');

    const parts = value
        .split(',')
        .map((part) => part.trim())
        .filter((part) => part.length > 0);

    // Minimum viable: street, city, state, zip.
    if (parts.length < 4) {
        return null;
    }

    const zip = parts[parts.length - 1];
    const state = parts[parts.length - 2];

    if (!ZIP_PATTERN.test(zip) || !STATE_PATTERN.test(state)) {
        return null;
    }

    const remaining = parts.slice(0, parts.length - 2);
    // Need at least a street segment and a city.
    if (remaining.length < 2) {
        return null;
    }

    const city = remaining[remaining.length - 1];
    const address = remaining.slice(0, remaining.length - 1).join(', ');

    // A real street address starts with a house/building number. This also
    // keeps PO-box-style and free-text entries out.
    if (!/^\d/.test(address)) {
        return null;
    }

    // City must contain at least one letter.
    if (!/[A-Za-z]/.test(city)) {
        return null;
    }

    const normalizedState = state.toUpperCase();

    return {
        address,
        city,
        state: normalizedState,
        zip,
        formatted: `${address}, ${city}, ${normalizedState} ${zip}`,
    };
}

export default parseUsAddress;
